import { cachedQuery, invalidateCache } from '../cache/queryCache';
import { ensureGymId, getCachedGymId } from '../receptionist/gymContext';
import { supabase, formatSupabaseError } from '../../supabase';
import { RECEPTIONIST_SALE_PAGES } from '../../pages/receptionist/sales/salePageConfig';
import { branchesMatch } from '../gym/branchContext';
import { fetchPayments } from '../receptionist/receptionistApi';
import { saleRowLeft } from '../../pages/receptionist/sales/salePaymentUtils';
import {
  computeDashboardSaleStats,
  dashboardPaymentAmount,
  dashboardPaymentDay,
  dashboardTodayKey,
  isDashboardGuestPassPayment,
  isDashboardNewMembershipPayment,
  isDashboardRenewalPayment,
} from '../shared/dashboardSaleMetrics';
import { fetchDashboardOverviewRpc } from '../shared/dashboardRpcApi';

function todayKey() {
  return dashboardTodayKey();
}

function filterPaymentsByBranch(rows, branchFilter) {
  if (!branchFilter || branchFilter === 'all') return rows || [];
  return (rows || []).filter((p) => {
    const b = String(p?.branch || p?.members?.branch || p?.branch_name || '').trim();
    if (!b) return true;
    return branchesMatch(b, branchFilter);
  });
}

function last7Days() {
  const days = [];
  for (let i = 6; i >= 0; i -= 1) {
    const d = new Date();
    d.setHours(12, 0, 0, 0);
    d.setDate(d.getDate() - i);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    days.push(`${y}-${m}-${day}`);
  }
  return days;
}

function last7Months() {
  const months = [];
  for (let i = 6; i >= 0; i -= 1) {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - i);
    months.push({
      key: d.toISOString().slice(0, 7),
      label: d.toLocaleDateString('en-US', { month: 'short' }),
    });
  }
  return months;
}

function daysBetween(start, end) {
  const days = [];
  if (!start || !end || end < start) return days;
  const cursor = new Date(`${start}T00:00:00`);
  const last = new Date(`${end}T00:00:00`);
  const maxDays = 62;
  let n = 0;
  while (cursor <= last && n < maxDays) {
    days.push(cursor.toISOString().slice(0, 10));
    cursor.setDate(cursor.getDate() + 1);
    n += 1;
  }
  return days;
}

function resolveChartDays(startDate, endDate) {
  if (startDate && endDate) {
    const range = daysBetween(startDate, endDate);
    if (range.length) return range;
  }
  if (startDate && !endDate) return daysBetween(startDate, todayKey());
  if (!startDate && endDate) {
    const start = new Date(`${endDate}T00:00:00`);
    start.setDate(start.getDate() - 6);
    return daysBetween(start.toISOString().slice(0, 10), endDate);
  }
  return last7Days();
}

function dayLabels(days) {
  if (days.length > 14) {
    return days.map((d) =>
      new Date(`${d}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    );
  }
  return days.map((d) =>
    new Date(`${d}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short' }),
  );
}

function monthsAgoIso(monthsBack) {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - monthsBack);
  return d.toISOString().slice(0, 10);
}

function hasDateFilter(startDate, endDate) {
  return Boolean(startDate || endDate);
}

function rangeStartIso(startDate) {
  return startDate ? `${startDate}T00:00:00` : null;
}

function rangeEndIso(endDate) {
  return endDate ? `${endDate}T23:59:59` : null;
}

function hasBranchScope(branchFilter) {
  const b = String(branchFilter || '').trim();
  return Boolean(b && b !== 'all');
}

/** Quote branch names (spaces, &) for PostgREST `.or()` filter strings. */
function formatPostgrestFilterValue(value) {
  const text = String(value ?? '');
  if (!text) return text;
  if (/[,\s().&]/.test(text) || text.includes('"')) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

function applyDirectBranchOr(query, branchFilter, column = 'branch') {
  if (!hasBranchScope(branchFilter)) return query;
  const quoted = formatPostgrestFilterValue(branchFilter);
  return query.or(`${column}.eq.${quoted},${column}.is.null`);
}

/** Lockers may stamp either branch or branch_name. */
function applyLockerBranchOr(query, branchFilter) {
  if (!hasBranchScope(branchFilter)) return query;
  const quoted = formatPostgrestFilterValue(branchFilter);
  return query.or(
    `branch.eq.${quoted},branch_name.eq.${quoted},and(branch.is.null,branch_name.is.null)`,
  );
}

function applyMembersEmbedBranchOr(query, branchFilter) {
  if (!hasBranchScope(branchFilter)) return query;
  const quoted = formatPostgrestFilterValue(branchFilter);
  // Blank member.branch stays visible — same as receptionist filterByReceptionistBranch
  return query.or(`members.branch.eq.${quoted},members.branch.is.null`);
}

/**
 * @param {string} table
 * @param {string} gid
 * @param {{ apply?: Function, branchFilter?: string, viaMembers?: boolean, selectExtra?: string }} [opts]
 */
async function countExact(table, gid, opts = {}) {
  const apply = typeof opts === 'function' ? opts : (opts.apply || ((q) => q));
  const branchFilter = typeof opts === 'function' ? '' : (opts.branchFilter || '');
  const viaMembers = typeof opts === 'function' ? false : Boolean(opts.viaMembers);
  const scoped = hasBranchScope(branchFilter) && viaMembers;
  const select = scoped ? 'id, members!inner(branch)' : '*';
  let query = supabase.from(table).select(select, { count: 'exact', head: true }).eq('gym_id', gid);
  if (scoped) query = applyMembersEmbedBranchOr(query, branchFilter);
  query = apply(query);
  const { count, error } = await query;
  if (error) throw error;
  return count || 0;
}

async function safeCount(table, gid, applyOrOpts, fallbackApply) {
  const opts = typeof applyOrOpts === 'function'
    ? { apply: applyOrOpts }
    : (applyOrOpts || {});
  try {
    return await countExact(table, gid, opts);
  } catch (err) {
    if (fallbackApply) {
      try {
        return await countExact(table, gid, { ...opts, apply: fallbackApply, viaMembers: false });
      } catch {
        return 0;
      }
    }
    console.warn(`Admin dashboard count failed for ${table}`, err?.message || err);
    return 0;
  }
}

async function safeSelect(builder) {
  const { data, error } = await builder;
  if (error) {
    console.warn('Admin dashboard select warning', error.message);
    return [];
  }
  return data || [];
}

function bucketByDay(rows, getDay, days) {
  const map = Object.fromEntries(days.map((d) => [d, 0]));
  for (const row of rows) {
    const day = getDay(row);
    if (day && day in map) map[day] += 1;
  }
  return days.map((d) => map[d]);
}

function sumByDay(rows, getDay, getAmount, days) {
  const map = Object.fromEntries(days.map((d) => [d, 0]));
  for (const row of rows) {
    const day = getDay(row);
    if (day && day in map) map[day] += Number(getAmount(row)) || 0;
  }
  return days.map((d) => map[d]);
}

function bucketByMonth(rows, getMonth, months) {
  const map = Object.fromEntries(months.map((m) => [m.key, 0]));
  for (const row of rows) {
    const key = getMonth(row);
    if (key && key in map) map[key] += 1;
  }
  return months.map((m) => map[m.key]);
}

function sumByMonth(rows, getMonth, getAmount, months) {
  const map = Object.fromEntries(months.map((m) => [m.key, 0]));
  for (const row of rows) {
    const key = getMonth(row);
    if (key && key in map) map[key] += Number(getAmount(row)) || 0;
  }
  return months.map((m) => map[m.key]);
}

function applyCreatedAtRange(q, startDate, endDate) {
  let next = q;
  const from = rangeStartIso(startDate);
  const to = rangeEndIso(endDate);
  if (from) next = next.gte('created_at', from);
  if (to) next = next.lte('created_at', to);
  return next;
}

/**
 * Fast admin dashboard via get_dashboard_overview RPC.
 * Falls back to PostgREST aggregates if the RPC is not deployed yet.
 * @param {{ startDate?: string, endDate?: string, branch?: string }} [range]
 */
export async function fetchAdminDashboardOverview(range = {}) {
  const gid = getCachedGymId() || await ensureGymId();
  if (!gid) {
    throw new Error(formatSupabaseError(null, 'Gym context is missing. Sign in again.'));
  }

  const startDate = range.startDate || '';
  const endDate = range.endDate || '';
  const branchFilter = String(range.branch || '').trim();
  const scopeLabel = branchFilter && branchFilter !== 'all'
    ? `Branch: ${branchFilter}`
    : 'All branches';

  invalidateCache(`gym:${gid}:admin-dashboard-`);

  return cachedQuery(
    `gym:${gid}:admin-dashboard-rpc-v1:${branchFilter || 'all'}:${startDate || 'all'}:${endDate || 'all'}`,
    async () => {
      try {
        const rpc = await fetchDashboardOverviewRpc({
          startDate,
          endDate,
          branch: branchFilter,
          panel: 'admin',
          scopeLabel,
        });
        if (rpc) return rpc;
      } catch (err) {
        console.warn('Admin dashboard RPC failed, using fallback', err?.message || err);
      }
      return fetchAdminDashboardOverviewLegacy(range, { gid, startDate, endDate, branchFilter, scopeLabel });
    },
    15_000,
  );
}

async function fetchAdminDashboardOverviewLegacy(range, ctx) {
  const gid = ctx.gid;
  const startDate = ctx.startDate;
  const endDate = ctx.endDate;
  const branchFilter = ctx.branchFilter;
  const scopeLabel = ctx.scopeLabel;
  const filtered = hasDateFilter(startDate, endDate);

  const today = todayKey();
    const days = resolveChartDays(startDate, endDate);
    const months = last7Months();
    const weekStart = days[0];
    const chartStart = filtered
      ? (startDate || monthsAgoIso(6))
      : monthsAgoIso(6);
    const soonEnd = new Date();
    soonEnd.setDate(soonEnd.getDate() + 14);
    const soonEndStr = soonEnd.toISOString().slice(0, 10);
    const branchOpts = { branchFilter, viaMembers: true };

    const attendanceApply = (q) => {
      let next = q;
      if (filtered) {
        if (startDate) next = next.gte('attendance_date', startDate);
        if (endDate) next = next.lte('attendance_date', endDate);
        return next;
      }
      return next.eq('attendance_date', today);
    };

    const [
      memberCount,
      packageCount,
      activeMemberships,
      expiringSoon,
      activeTrainers,
      activeReceptionists,
      attendanceCount,
      classCount,
      equipmentCount,
      lockerCount,
      freezeCount,
      allPayments,
      weekAttendance,
      membershipEnds,
      memberJoins,
      weekGuests,
      assignedLockers,
    ] = await Promise.all([
      filtered
        ? safeCount('members', gid, {
          apply: (q) => applyDirectBranchOr(applyCreatedAtRange(q, startDate, endDate), branchFilter),
        })
        : safeCount('members', gid, {
          apply: (q) => applyDirectBranchOr(q, branchFilter),
        }),
      safeCount('packages', gid),
      safeCount(
        'memberships',
        gid,
        {
          ...branchOpts,
          apply: (q) => q.eq('status', 'active').eq('frozen', false),
        },
        (q) => q.eq('status', 'active'),
      ),
      safeCount(
        'memberships',
        gid,
        {
          ...branchOpts,
          apply: (q) => q.eq('status', 'active').gte('end_date', today).lte('end_date', soonEndStr),
        },
        (q) => q.eq('status', 'active'),
      ),
      safeCount('trainers', gid, {
        apply: (q) => applyDirectBranchOr(q.eq('status', 'active'), branchFilter),
      }, (q) => applyDirectBranchOr(q, branchFilter)),
      safeCount('receptionists', gid, (q) => q.eq('status', 'active'), (q) => q),
      safeCount(
        'attendance_logs',
        gid,
        { ...branchOpts, apply: attendanceApply },
        (q) => applyCreatedAtRange(q, startDate || today, endDate || today),
      ),
      safeCount('classes', gid),
      safeCount('equipments', gid),
      safeCount('lockers', gid, {
        apply: (q) => applyLockerBranchOr(q, branchFilter),
      }),
      filtered
        ? safeCount('membership_freezes', gid, {
          ...branchOpts,
          apply: (q) => applyCreatedAtRange(q, startDate, endDate),
        })
        : safeCount('membership_freezes', gid, {
          ...branchOpts,
          apply: (q) => q.eq('status', 'active'),
        }, (q) => q),
      // Same paged payment source + mapper as Reception dashboard
      fetchPayments().catch(() => []),
      safeSelect(
        (() => {
          let q = supabase
            .from('attendance_logs')
            .select(hasBranchScope(branchFilter) ? 'attendance_date, created_at, members!inner(branch)' : 'attendance_date, created_at')
            .eq('gym_id', gid)
            .gte('created_at', `${weekStart}T00:00:00`);
          if (hasBranchScope(branchFilter)) q = applyMembersEmbedBranchOr(q, branchFilter);
          if (endDate) q = q.lte('created_at', rangeEndIso(endDate));
          return q;
        })(),
      ),
      safeSelect(
        (() => {
          let q = supabase
            .from('memberships')
            .select(hasBranchScope(branchFilter) ? 'end_date, members!inner(branch)' : 'end_date')
            .eq('gym_id', gid)
            .gte('end_date', chartStart);
          if (hasBranchScope(branchFilter)) q = applyMembersEmbedBranchOr(q, branchFilter);
          return q;
        })(),
      ),
      safeSelect(
        (() => {
          let q = supabase
            .from('members')
            .select('joining_date, created_at')
            .eq('gym_id', gid)
            .gte('created_at', `${chartStart}T00:00:00`);
          q = applyDirectBranchOr(q, branchFilter);
          if (endDate) q = q.lte('created_at', rangeEndIso(endDate));
          return q;
        })(),
      ),
      safeSelect(
        (() => {
          let q = supabase
            .from('guest_passes')
            .select('created_at')
            .eq('gym_id', gid)
            .gte('created_at', `${weekStart}T00:00:00`);
          if (endDate) q = q.lte('created_at', rangeEndIso(endDate));
          return q;
        })(),
      ),
      safeCount('lockers', gid, {
        apply: (q) => applyLockerBranchOr(q.eq('status', 'assigned'), branchFilter),
      }, (q) => applyLockerBranchOr(q, branchFilter)),
    ]);

    const scopedPayments = filterPaymentsByBranch(allPayments, branchFilter);
    const saleMetrics = computeDashboardSaleStats(scopedPayments, {
      startDate,
      endDate,
      filtered,
      scopeHint: scopeLabel,
    });

    const remainingCount = scopedPayments.filter((p) => saleRowLeft(p) > 0).length;

    const paymentDay = (p) => dashboardPaymentDay(p);
    const paymentMonth = (p) => dashboardPaymentDay(p).slice(0, 7);
    const memberMonth = (m) => String(m.joining_date || m.created_at || '').slice(0, 7);
    const attendanceDay = (a) => String(a.attendance_date || a.created_at || '').slice(0, 10);
    const guestDay = (g) => String(g.created_at || '').slice(0, 10);

    const chartPayments = scopedPayments.filter((p) => {
      const d = paymentDay(p);
      if (!d) return false;
      if (filtered) {
        if (startDate && d < startDate) return false;
        if (endDate && d > endDate) return false;
        return true;
      }
      return d >= chartStart;
    });

    const revenueValues = sumByMonth(chartPayments, paymentMonth, dashboardPaymentAmount, months);

    const chartDayPayments = filtered
      ? chartPayments
      : chartPayments.filter((p) => days.includes(paymentDay(p)));

    const categorySlices = [];
    const newMemChartAmt = chartDayPayments
      .filter((p) => isDashboardNewMembershipPayment(p))
      .reduce((sum, p) => sum + dashboardPaymentAmount(p), 0);
    if (newMemChartAmt > 0) categorySlices.push({ label: 'New Membership', value: newMemChartAmt });
    for (const page of RECEPTIONIST_SALE_PAGES) {
      const amt = chartDayPayments
        .filter((p) => String(p.source || '').toLowerCase() === page.source)
        .reduce((sum, p) => sum + dashboardPaymentAmount(p), 0);
      if (amt > 0) categorySlices.push({ label: page.label, value: amt });
    }
    const guestAmt = chartDayPayments
      .filter((p) => isDashboardGuestPassPayment(p))
      .reduce((sum, p) => sum + dashboardPaymentAmount(p), 0);
    if (guestAmt > 0) categorySlices.push({ label: 'Guest Pass', value: guestAmt });

    const renewalChart = chartDayPayments.filter((p) => isDashboardRenewalPayment(p));
    const newMemChart = chartDayPayments.filter((p) => isDashboardNewMembershipPayment(p));

    const stats = [
      {
        label: filtered ? 'New Members (range)' : 'Total Members',
        value: String(memberCount),
        change: filtered ? scopeLabel : `${scopeLabel} · all-time`,
        key: 'members',
      },
      saleMetrics.newMembershipStat,
      saleMetrics.newMembershipAmountStat,
      saleMetrics.registrationFeeStat,
      { label: 'Packages', value: String(packageCount), change: 'Gym-wide', key: 'packages' },
      {
        label: 'Active Memberships',
        value: String(activeMemberships),
        change: expiringSoon
          ? `${scopeLabel} · ${expiringSoon} expiring soon`
          : scopeLabel,
        key: 'memberships',
        variant: expiringSoon ? 'warning' : undefined,
      },
      {
        label: 'Trainers',
        value: String(activeTrainers),
        change: `${scopeLabel} · active`,
        key: 'trainers',
      },
      {
        label: 'Receptionists',
        value: String(activeReceptionists),
        change: 'Gym-wide · active',
        key: 'receptionists',
      },
      {
        label: filtered ? 'Attendance (range)' : "Today's Attendance",
        value: String(attendanceCount),
        change: filtered ? scopeLabel : `${scopeLabel} · today`,
        key: 'attendance',
      },
      saleMetrics.revenueStatAdmin,
      saleMetrics.paymentsCountStat,
      saleMetrics.guestPassStat,
      { label: 'Classes', value: String(classCount), change: 'Gym-wide', key: 'classes' },
      { label: 'Equipment', value: String(equipmentCount), change: 'Gym-wide', key: 'equipments' },
      {
        label: 'Assigned Lockers',
        value: String(assignedLockers),
        change: lockerCount ? `${scopeLabel} · ${lockerCount} total` : scopeLabel,
        key: 'lockers',
      },
      {
        label: filtered ? 'Freezes (range)' : 'Active Freezes',
        value: String(freezeCount),
        change: filtered ? scopeLabel : `${scopeLabel} · active now`,
        key: 'freeze',
      },
      ...saleMetrics.saleStats,
      {
        label: 'Remaining Payments (open)',
        value: String(remainingCount),
        change: `${scopeLabel} · outstanding balance`,
        key: 'remaining',
        variant: remainingCount ? 'warning' : undefined,
      },
      { label: 'Payment Methods', value: '—', change: 'Configure in settings', key: 'paymentMethods' },
    ];

    const charts = {
      revenue: {
        labels: months.map((m) => m.label),
        values: revenueValues,
      },
      attendance: {
        labels: dayLabels(days),
        values: bucketByDay(weekAttendance, attendanceDay, days),
      },
      renewals: {
        labels: months.map((m) => m.label),
        values: bucketByMonth(membershipEnds, (m) => String(m.end_date || '').slice(0, 7), months),
      },
      registrations: {
        labels: months.map((m) => m.label),
        values: bucketByMonth(memberJoins, memberMonth, months),
      },
      invoices: {
        labels: dayLabels(days),
        values: sumByDay(chartPayments, paymentDay, dashboardPaymentAmount, days),
      },
      visitors: {
        labels: dayLabels(days),
        values: bucketByDay(weekGuests, guestDay, days),
      },
      finance: {
        labels: months.map((m) => m.label),
        income: revenueValues,
        expenses: months.map(() => 0),
      },
      categorySales: {
        labels: categorySlices.map((s) => s.label),
        values: categorySlices.map((s) => s.value),
      },
      dailyCollection: {
        labels: dayLabels(days),
        values: sumByDay(chartDayPayments, paymentDay, dashboardPaymentAmount, days),
      },
      salesByDay: {
        labels: dayLabels(days),
        values: sumByDay(chartDayPayments, paymentDay, dashboardPaymentAmount, days),
      },
      membershipVsRenewal: {
        labels: ['New Membership', 'Renewal'],
        values: [
          newMemChart.reduce((sum, p) => sum + dashboardPaymentAmount(p), 0),
          renewalChart.reduce((sum, p) => sum + dashboardPaymentAmount(p), 0),
        ],
      },
    };

  return { stats, charts, scopeLabel };
}
